// Ekran 10 — Kontrol Paneli ("Tesis Nabzı").
// Yerleşim ve sınıf adları ref_files/btys-erp-10-kontrol-paneli.html'den birebir:
//   .crumb · .page-h · .kstrip(5 KPI) · .adrow(CR kartları)
//   .dash( sol: 5 ışık · grafik · bantlar | sağ: .darkp · .cumrow · uyarı · stok · feed )
//
// Referans mockup'ta bu ekranın 109 sayısının tamamı HTML'e gömülüydü; hepsi
// /api/panel/ozet'e bağlandı. Ekranda hesap YOK — K29 dengesizliği, eşik
// bantları ve Δ% sunucudan geliyor (bkz. docs/kararlar-ek.md).

import { Suspense, lazy, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { toast } from '../../components/Toast'
import { tr } from '../../lib/trFormat'
import {
  panel,
  type Delta,
  type KpiKutusu,
  type PanelBandi,
  type PanelOzeti,
} from './api'

/**
 * ApexCharts (~150 KB gzip) YALNIZ bu ekranda kullanılıyor; tembel yüklenince
 * diğer 11 ekranın ilk paketi büyümüyor.
 */
const UretimGrafigi = lazy(() => import('./UretimGrafigi'))

/** Seed'in gün hikâyesi 13–15.08; ekran 06/07/08 ile aynı desen. */
const VARSAYILAN_TARIH = '2026-08-15'

const GUN_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

function gunEtiketi(iso: string): string {
  const [y, a, g] = iso.split('-').map(Number)
  const d = new Date(Date.UTC(y, a - 1, g))
  return `${String(g).padStart(2, '0')}.${String(a).padStart(2, '0')} ${GUN_ADI[d.getUTCDay()]}`
}

const SEGMENT_RENK: Record<string, string> = {
  saglikli: '#E3ECF7',
  izle: 'var(--amber-bg)',
  kritik: 'var(--red-bg)',
}

/** KPI değerinin sınıfı — bant sunucudan (§6.3 eşikleri). */
function kpiSinif(bant: string | null | undefined): string {
  if (bant === 'kritik') return 'k-v red'
  if (bant === 'izle') return 'k-v amber'
  return 'k-v'
}

function DeltaRozeti({ delta }: { delta: Delta | null }) {
  if (!delta || delta.yuzde === null) return null
  const ok = delta.yon === 'up' ? '↑' : delta.yon === 'dn' ? '↓' : '→'
  return (
    <span className={`dlt ${delta.yon}`} title={delta.etiket}>
      {ok} {tr(Math.abs(delta.yuzde), 1)}%
    </span>
  )
}

/**
 * KPI şeridinin bir kutusu. Satır içi flex YALNIZ spark taşıyan kutuda var
 * (ref:484); diğer dördünde (ref:485-488) düz metin akışı, yoksa `.dlt` rozeti
 * sayının taban çizgisinden kopup 8px boşlukla ayrılıyor.
 */
function KpiKutu({ k, cocuk }: { k: KpiKutusu; cocuk?: React.ReactNode }) {
  return (
    <div className="k">
      <div className="k-l">{k.etiket}</div>
      <div
        className={kpiSinif(k.bant)}
        style={cocuk ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}
      >
        {k.isaretli ? isaretli(k.deger) : tr(k.deger, k.basamak)}
        {cocuk}
        {/* Flex'siz kutuda sayı ile rozet arasını referanstaki gibi metin boşluğu ayırır. */}
        {cocuk ? null : ' '}
        <DeltaRozeti delta={k.delta} />
      </div>
      <div className="k-s">{k.altMetin}</div>
    </div>
  )
}

/** Anlık güç kutusundaki mini çizgi (.spk) — sunucudan gelen saatlik seri. */
function Spark({ seri }: { seri: (number | null)[] }) {
  const dolu = seri.filter((v): v is number => v !== null)
  if (dolu.length < 2) return null

  const enBuyuk = Math.max(...dolu)
  const enKucuk = Math.min(...dolu)
  const aralik = enBuyuk - enKucuk || 1
  const noktalar = seri
    .map((v, i) => (v === null ? null : `${(i / (seri.length - 1)) * 74},${22 - ((v - enKucuk) / aralik) * 18}`))
    .filter(Boolean)
    .join(' ')

  return (
    <svg className="spk" viewBox="0 0 74 26" preserveAspectRatio="none">
      <polyline points={noktalar} fill="none" stroke="#2E5EA8" strokeWidth="2" />
    </svg>
  )
}

/** Proses göstergesinin renkli bandı — dilim sınırları ayar_esik'ten geliyor. */
function BantSatiri({ b }: { b: PanelBandi }) {
  const oran = (x: number) => ((x - b.min) / (b.maks - b.min || 1)) * 100

  return (
    <div className="band-row">
      <span className="bl">{b.etiket}</span>
      <span className="band">
        {b.segmentler.map((s, i) => (
          <span
            key={i}
            className="seg"
            style={{
              left: `${oran(s.bas)}%`,
              width: `${oran(s.son) - oran(s.bas)}%`,
              background: SEGMENT_RENK[s.bant] ?? '#E3ECF7',
              borderRadius: 99,
            }}
          />
        ))}
        {b.deger !== null ? (
          <span className="mk" style={{ left: `calc(${Math.min(100, Math.max(0, oran(b.deger)))}% - 1px)` }} />
        ) : null}
      </span>
      <span className="bv" title={`Hedef: ${b.hedef}`}>
        {b.degerMetni}
      </span>
    </div>
  )
}

function DisAciIkonu() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
    </svg>
  )
}

function Icerik() {
  const yonlendir = useNavigate()
  const [mod, setMod] = useState<'saat' | 'gun' | 'ay'>('saat')

  const sorgu = useQuery({
    queryKey: ['panel', VARSAYILAN_TARIH],
    queryFn: () => panel.ozet(VARSAYILAN_TARIH),
  })

  const o: PanelOzeti | undefined = sorgu.data

  const panelSaati = useMemo(
    () =>
      o
        ? new Date(o.panelZamani).toLocaleTimeString('tr-TR', {
            timeZone: 'Europe/Istanbul',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '—',
    [o],
  )

  // `.pane` sarmalayıcısı SekmePanolari'nın işi; referansta `main.content`
  // altında tek katman var (ref:472-473). Buradaki içerik doğrudan onun çocuğu.
  if (sorgu.isLoading) {
    return (
      <>
        <div className="crumb">
          <b>Kontrol Paneli</b>
        </div>
        <div className="card">
          <div className="insp-empty">Panel yükleniyor…</div>
        </div>
      </>
    )
  }

  if (sorgu.isError || !o) {
    return (
      <>
        <div className="crumb">
          <b>Kontrol Paneli</b>
        </div>
        <div className="card">
          <div className="insp-empty">Panel verisi alınamadı — {String(sorgu.error ?? 'bilinmeyen hata')}</div>
        </div>
      </>
    )
  }

  const seri = o.grafik[mod]

  return (
    <>
      <div className="crumb">
        <b>Kontrol Paneli</b> · {gunEtiketi(o.tarih)}, {panelSaati}
      </div>
      <div className="page-h">
        <h1 className="page-t">Tesis Nabzı</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={async () => {
              await sorgu.refetch()
              toast(`Panel yenilendi — ${panelSaati}`)
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-3-6.7M21 3v6h-6" />
            </svg>
            Yenile
          </button>
          <button className="btn btn-pri" type="button" onClick={() => toast(o.kapanis.mesaj)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3 8-8M21 12a9 9 0 1 1-9-9" />
            </svg>
            Gün sonu kapanışını çalıştır
          </button>
        </div>
      </div>

      <div className="kstrip">
        <KpiKutu k={o.kpi.anlikGuc} cocuk={<Spark seri={o.kpi.spark} />} />
        <KpiKutu k={o.kpi.uretim} />
        <KpiKutu k={o.kpi.biyogaz} />
        <KpiKutu k={o.kpi.besleme} />
        <KpiKutu k={o.kpi.dengesizlik} />
      </div>

      <div className="adrow">
        {o.adrow.map((a) => (
          <div className="adc" key={a.uniteId}>
            <div className="adk">
              <b>{a.kod}</b>
              <span className={`ld ${a.isik}`} style={{ width: 8, height: 8 }} />
            </div>
            <div className="adb">
              <div className="adv">
                <div className="adl">Gaz (00–{String(o.sinirSaat).padStart(2, '0')})</div>
                <div className="adn">
                  {tr(a.gaz, 0)}
                  <small> Nm³</small>
                </div>
                <div className="ads">
                  <DeltaRozeti delta={a.delta} /> dünden
                </div>
              </div>
              <div className="adv">
                <div className="adl">Besleme</div>
                <div className="adn">
                  {tr(a.beslemeT, 1)}
                  <small> t</small>
                </div>
                <div className="ads">beyan K20</div>
              </div>
              <div className="adv">
                <div className="adl">Sıcaklık · Doluluk</div>
                <div className="adn" style={{ fontSize: 13 }}>
                  {a.sicaklik === null ? '—' : `${tr(a.sicaklik, 1)} °C`}
                </div>
                <div className="ads">
                  {a.doluluk === null ? '—' : `%${tr(a.doluluk, 0)}`} doluluk
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dash">
        <div>
          <div className="card">
            <div className="sect-t">Gün Sonu Kapanışı — 5 Işık (A13)</div>
            <div>
              {o.isiklar.map((l) => (
                <div className="light" key={l.anahtar}>
                  <span className={`ld ${l.durum}`} />
                  <div className="lt">
                    <b>{l.baslik}</b>
                    <div className="ls">{l.alt}</div>
                  </div>
                  <button
                    className="mini-btn"
                    type="button"
                    title={`${l.modul} ekranını aç`}
                    onClick={() => yonlendir(l.rota)}
                  >
                    <DisAciIkonu />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="toolbar">
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy-800)' }}>
                Üretim Grafiği — elektrik ↔ biyogaz
              </span>
              <div className="toolbar-right">
                <div className="seg">
                  {(['saat', 'gun', 'ay'] as const).map((k) => (
                    <button
                      key={k}
                      className={mod === k ? 'seg-i on' : 'seg-i'}
                      type="button"
                      onClick={() => setMod(k)}
                    >
                      {o.grafik[k].etiket}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Suspense
              fallback={
                <div className="chart-w" style={{ height: 236 }}>
                  <div className="insp-empty">Grafik yükleniyor…</div>
                </div>
              }
            >
              <UretimGrafigi seri={seri} />
            </Suspense>
            <div className="pnote">{seri.not}</div>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="sect-t">Proses Göstergeleri — canlı bantlar</div>
            <div>
              {o.bantlar.length === 0 ? (
                <div className="insp-empty">Bant çizilecek gösterge yok.</div>
              ) : (
                o.bantlar.map((b) => <BantSatiri b={b} key={b.gosterge} />)
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="darkp">
            <div className="dp-h">
              <span>⚡ Son Kayıt Verileri</span>
              <span className="dp-t">{o.sonKayit.veriSaati}</span>
            </div>
            <div className="dp-s">Gaz Parametreleri</div>
            {o.sonKayit.gaz.map((r) => (
              <div className="dp-r" key={r.etiket}>
                <span>{r.etiket}</span>
                <b style={r.bant && r.bant !== 'saglikli' ? { color: '#F5B84D' } : undefined}>
                  {tr(r.deger, r.basamak)}
                  <small>
                    {r.birim}
                    {r.not ? ` · ${r.not}` : ''}
                  </small>
                </b>
              </div>
            ))}
            <div className="dp-s">
              Üretim Parametreleri
              {o.sonDoluSaat === null ? '' : ` — son saat (${String(o.sonDoluSaat).padStart(2, '0')}:00)`}
            </div>
            {o.sonKayit.uretim.map((r) => (
              <div className="dp-r" key={r.etiket}>
                <span>
                  {r.etiket}
                  {r.not ? ` (${r.not})` : ''}
                </span>
                <b>
                  {tr(r.deger, r.basamak)}
                  <small>{r.birim}</small>
                </b>
              </div>
            ))}
            <div className="dp-r" style={{ paddingBottom: 10 }}>
              <span>Dengesizlik / taahhüt sapması</span>
              <b style={{ color: '#7FB0E8' }}>
                {isaretli(o.sonKayit.dengesizlik)} / {isaretli(o.sonKayit.taahhutSapmasi)}
                <small>kWh</small>
              </b>
            </div>
          </div>

          <div className="cumrow">
            <div className="cum">
              {/* Başlık dönemi de yazar ("Bu Ay — Ağustos", ref:520); ay adı sunucudan. */}
              <div className="cm-h">{o.kumulatif.ay.baslik}</div>
              <div className="cm-b">
                <div>
                  <div className="cm-v">{tr(o.kumulatif.ay.uretimKwh, 0)}</div>
                  <div className="cm-l">kWh üretim</div>
                </div>
                <div>
                  <div className="cm-v">{tr(o.kumulatif.ay.biyogazNm3, 0)}</div>
                  <div className="cm-l">Nm³ biyogaz</div>
                </div>
                <div>
                  <div className="cm-v">{tr(o.kumulatif.ay.beslemeT, 0)}</div>
                  <div className="cm-l">t besleme</div>
                </div>
                <div>
                  <div className="cm-v">
                    {o.kumulatif.ay.kapasiteFaktoru === null
                      ? '—'
                      : `%${tr(o.kumulatif.ay.kapasiteFaktoru, 0)}`}
                  </div>
                  <div className="cm-l">kapasite faktörü</div>
                </div>
              </div>
            </div>
            <div className="cum">
              <div className="cm-h">{o.kumulatif.yil.baslik}</div>
              <div className="cm-b">
                <div>
                  <div className="cm-v">{tr(bine(o.kumulatif.yil.uretimKwh), 0)}</div>
                  <div className="cm-l">MWh üretim</div>
                </div>
                <div>
                  <div className="cm-v">{tr(bine(o.kumulatif.yil.biyogazNm3), 0)}</div>
                  <div className="cm-l">bin Nm³ biyogaz</div>
                </div>
                <div>
                  <div className="cm-v">{tr(o.kumulatif.yil.beslemeT, 0)}</div>
                  <div className="cm-l">t besleme</div>
                </div>
                <div>
                  <div className="cm-v">
                    {o.kumulatif.yil.kapasiteFaktoru === null
                      ? '—'
                      : `%${tr(o.kumulatif.yil.kapasiteFaktoru, 0)}`}
                  </div>
                  <div className="cm-l">kapasite faktörü</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="sect-t">
              Uyarılar &amp; Görevler
              <span className="badge b-amber" style={{ marginLeft: 6 }}>
                {o.uyarilar.length} açık
              </span>
            </div>
            <div>
              {o.uyarilar.length === 0 ? (
                <div className="insp-empty">Açık uyarı yok.</div>
              ) : (
                o.uyarilar.map((u, i) => (
                  <div
                    className="uyari-li"
                    key={`${u.kuralRef}-${i}`}
                    onClick={() => (u.rota ? yonlendir(u.rota) : undefined)}
                    style={u.rota ? { cursor: 'pointer' } : undefined}
                  >
                    <span
                      className="ui"
                      style={{ color: u.sev === 'red' ? 'var(--red)' : 'var(--amber-dot)' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
                      </svg>
                    </span>
                    <span style={{ flex: 1 }}>
                      {/* Başlığın yanındaki `.cell-sub` SAATİ taşır ve saat yoksa
                          hiç basılmaz (ref:648); kural referansı metnin içinde. */}
                      <b style={{ fontSize: 11 }}>{u.baslik}</b>
                      {u.saat ? (
                        <>
                          {' '}
                          <span className="cell-sub">· {u.saat}</span>
                        </>
                      ) : null}
                      <div className="cell-sub" style={{ marginTop: 1 }}>
                        {u.metin}
                      </div>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="sect-t">Kritik Stoklar</div>
            <table className="ptab">
              <thead>
                <tr>
                  <th>Kart / Havuz</th>
                  <th className="num">Bakiye</th>
                  <th className="num">Kritik</th>
                  <th style={{ width: 70 }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {o.kritikStok.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ color: 'var(--faint)' }}>
                      Kritik seviyesi tanımlı kart yok.
                    </td>
                  </tr>
                ) : (
                  o.kritikStok.map((k) => (
                    <tr key={k.stokKartiId} onClick={() => yonlendir('/stok-hareketleri')}>
                      <td style={{ fontSize: 11.5 }}>
                        {k.kod} {k.ad}
                      </td>
                      <td className="num" style={{ fontWeight: 600 }}>
                        {tr(k.bakiye, 1)} {k.birim}
                      </td>
                      <td className="num" style={{ color: 'var(--faint)' }}>
                        {tr(k.kritik, 0)} {k.birim}
                      </td>
                      <td>
                        {k.durum === 'kritik_alti' ? (
                          <span className="badge b-amber">kritik altı</span>
                        ) : (
                          <span className="badge b-navy">yeterli</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="sect-t">Son Hareketler</div>
            <div className="mini-list" style={{ padding: '4px 12px 8px' }}>
              {/* Referans feed'i stornolanmış satırı ayırmıyor (ref:672) ve
                  `.storno-r` kuralı yalnız `tr`ye işliyor — div'de ölü sınıftı. */}
              {o.feed.map((f) => (
                <div
                  className="mini-li"
                  key={f.id}
                  onClick={() => yonlendir('/stok-hareketleri')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="dt">
                    {new Date(f.zaman).toLocaleTimeString('tr-TR', {
                      timeZone: 'Europe/Istanbul',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="blg" style={{ marginRight: 6 }}>
                    {f.belgeNo}
                  </span>
                  {f.aciklama}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/** ± işaretli gösterim — dengesizlik yönü işaretiyle okunur (K29). */
function isaretli(v: number | null): string {
  if (v === null) return '—'
  return `${v > 0 ? '+' : ''}${tr(v, 0)}`
}

function bine(v: number | null): number | null {
  return v === null ? null : v / 1000
}

export default function PanelSayfasi() {
  return (
    <Kabuk listeBasligi="Kontrol Paneli">
      <Icerik />
    </Kabuk>
  )
}
